// What a session's transcript says beside its items (transcript/acp.ts draws
// none of it): the agent's own title for the session, its last ACP
// `session_info_update` that names one; the commands it offers, its last
// `available_commands_update`; and the first prompt sent to it. The Code tab
// names a session by them when no issue does, and lists the commands when
// the prompt box holds a slash. Every payload is read as untrusted.
import type { TranscriptItem } from '../api/types'

/** A command the agent offers, as ACP names it: `/name`, what it does, and what follows its name. */
export interface AgentCommand {
  name: string
  description: string
  /** What follows the name, as the agent words it (`<model>`); empty when it takes nothing. */
  hint: string
}

export interface SessionInfo {
  /** The agent's own title; null until it names one, or once it clears it. */
  title: string | null
  /** The commands it offers; null until it says. */
  commands: readonly AgentCommand[] | null
  /** The words of the first prompt sent; null until one is read. */
  firstPrompt: string | null
}

export const NO_INFO: SessionInfo = { title: null, commands: null, firstPrompt: null }

type Json = Record<string, unknown>

const isRecord = (value: unknown): value is Json => typeof value === 'object' && value !== null && !Array.isArray(value)

/** The commands an `available_commands_update` lists; those that are not one are skipped. */
function commandsOf(value: unknown): AgentCommand[] {
  if (!Array.isArray(value)) return []
  return value.flatMap((entry): AgentCommand[] => {
    if (!isRecord(entry) || typeof entry.name !== 'string') return []
    const name = entry.name.trim().replace(/^\//, '')
    if (name === '' || /\s/.test(name)) return []
    const input = isRecord(entry.input) ? entry.input : {}
    return [{ name, description: typeof entry.description === 'string' ? entry.description : '', hint: typeof input.hint === 'string' ? input.hint : '' }]
  })
}

/** The info once these items, which follow those it was made of, are in; the same object when they change nothing. */
export function infoOf(info: SessionInfo, items: readonly TranscriptItem[]): SessionInfo {
  let { title, commands, firstPrompt } = info
  for (const item of items) {
    const payload = item.payload
    if (!isRecord(payload) || payload.engine_subagent === true) continue
    if (item.source === 'sent') {
      if (firstPrompt === null && typeof payload.prompt === 'string') firstPrompt = payload.prompt
      continue
    }
    const update = payload.update
    if (!isRecord(update)) continue
    if (update.sessionUpdate === 'session_info_update' && 'title' in update) {
      const named = typeof update.title === 'string' ? update.title.trim() : ''
      title = named === '' ? null : named
    } else if (update.sessionUpdate === 'available_commands_update') {
      commands = commandsOf(update.availableCommands)
    }
  }
  return title === info.title && commands === info.commands && firstPrompt === info.firstPrompt ? info : { title, commands, firstPrompt }
}
